import React, { useEffect, useState, Suspense, lazy, useRef, useCallback } from 'react';
import ProductGrid from './components/ProductGrid';
import './App.css';
import { Routes, Route, Link, useLocation, Navigate, Outlet } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { CartProvider, useCart, formatCurrency, bogoDiscountForItems } from './context/CartContext';
import { ToastProvider, useToast } from './context/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';

// Set to false to close the online shop (hides browsing, cart and checkout).
const SHOP_ENABLED = true;

const ENTRY_LOCK_FORCE = false;
const ENTRY_LOCK_ENABLED = ENTRY_LOCK_FORCE || (process.env.REACT_APP_ENTRY_LOCK === '1');

// Lazy route components (code-splitting)
const ContactPage = lazy(() => import(/* webpackChunkName: "contact" */ './pages/ContactPage'));
const AboutPage = lazy(() => import(/* webpackChunkName: "about" */ './pages/AboutPage'));
const CartPage = lazy(() => import(/* webpackChunkName: "cart" */ './pages/CartPage'));
const BooksPage = lazy(() => import(/* webpackChunkName: "books" */ './pages/BooksPage'));
const ResourcesPage = lazy(() => import(/* webpackChunkName: "resources" */ './pages/ResourcesPage'));
const EntryPage = lazy(() => import(/* webpackChunkName: "entry" */ './pages/EntryPage'));

// Preload helpers for lazy routes (hover/focus prefetch)
const preloadContact = () => import(/* webpackPrefetch: true */ './pages/ContactPage');
const preloadAbout = () => import(/* webpackPrefetch: true */ './pages/AboutPage');
const preloadCart = () => import(/* webpackPrefetch: true */ './pages/CartPage');

function ShopClosed(){
  return (
    <div style={{padding:'80px 20px', textAlign:'center', maxWidth:560, margin:'0 auto'}}>
      <h1 style={{fontSize:20, letterSpacing:'.08em', margin:'0 0 12px'}}>THE SHOP IS CURRENTLY CLOSED</h1>
      <p style={{fontSize:13, lineHeight:1.6, opacity:.8, margin:'0 0 8px'}}>
        Online ordering is paused. Everything is being sold in person right now.
      </p>
      <p style={{fontSize:13, lineHeight:1.6, opacity:.8}}>
        <Link to="/contact" style={{textDecoration:'underline'}}>Get in touch</Link> if you want something held.
      </p>
    </div>
  );
}

function RequireEntered({ entered }: { entered: boolean }){
  return entered ? <Outlet /> : <Navigate to="/" replace />;
}

async function startCheckout(items: {id:string; priceId?: string}[], isInternational?: boolean) {
  try {
    const res = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type':'application/json' },
      body: JSON.stringify({ items, isInternational: !!isInternational })
    });
    if(!res.ok) throw new Error('Failed to start checkout');
    const data = await res.json();
    if(data.url) {
      window.location.href = data.url;
    } else {
      alert('No checkout URL returned.');
    }
  } catch(err:any){
    alert(err.message || 'Checkout error');
  }
}

// Extracted pages moved to /pages and lazy loaded
// MiniCart unchanged
function MiniCart({ open, onClose }: { open: boolean; onClose: ()=>void }){
  const { items, removeFromCart, totalPrice } = useCart();
  const bogo = bogoDiscountForItems(items);
  const estimatedTotal = Math.max(0, totalPrice - bogo);
  const { addToast } = useToast();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const firstFocusable = useRef<HTMLElement | null>(null);
  const lastFocusable = useRef<HTMLElement | null>(null);
  const [bump, setBump] = useState(false);
  const [intl, setIntl] = useState<boolean>(() => {
    try { return localStorage.getItem('rb_ship_intl') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('rb_ship_intl', intl ? '1' : '0'); } catch {}
  }, [intl]);

  // Helper to compute back image from a front image path
  const toBackImage = useCallback((url: string) => {
    if(!url) return url;
    const base = url.split('?')[0];
    if(base.includes('-FRONT.')) return base.replace('-FRONT.', '-BACK.');
    // Fallback: return original if pattern unknown
    return base;
  }, []);

  // Focus trap logic when open
  useEffect(() => {
    if(open && dialogRef.current){
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      firstFocusable.current = focusable[0] || null;
      lastFocusable.current = focusable[focusable.length - 1] || null;
      firstFocusable.current?.focus();
      const handleKey = (e: KeyboardEvent) => {
        if(e.key === 'Escape'){ onClose(); }
        if(e.key === 'Tab' && focusable.length){
          if(e.shiftKey && document.activeElement === firstFocusable.current){ e.preventDefault(); lastFocusable.current?.focus(); }
          else if(!e.shiftKey && document.activeElement === lastFocusable.current){ e.preventDefault(); firstFocusable.current?.focus(); }
        }
      };
      document.addEventListener('keydown', handleKey);
      return () => document.removeEventListener('keydown', handleKey);
    }
  }, [open, onClose]);

  // Prevent background scroll when mini cart open
  useEffect(() => { if(open){ document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; } }, [open]);

  useEffect(() => { if(open){ setBump(true); const t = setTimeout(()=> setBump(false), 450); return () => clearTimeout(t);} }, [totalPrice, open]);

  if(!open) return null;
  return (
    <>
      <div className="mini-cart-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="mini-cart" ref={dialogRef} role="dialog" aria-label="Cart" aria-modal="true">
        <header>
          <span>CART ({items.length})</span>
          <button className="close-btn" onClick={onClose} aria-label="Close cart">✕</button>
        </header>
        <div className="items">
          {items.length>0 && (
            <div className="bogo-banner">
              <strong>BUY ONE GET ONE FREE</strong>
              <span>{items.length < 2 ? 'Add one more — cheaper item free.' : 'Cheaper item in every pair is free.'}</span>
            </div>
          )}
          {items.length===0 && <p style={{fontSize:12}}>Empty</p>}
          {items.map(it => (
            <div className="item" key={it.id}>
              <img
                src={it.image}
                alt={it.name}
                width={60}
                height={60}
                onMouseEnter={(e) => { try { (e.currentTarget as HTMLImageElement).src = toBackImage(it.image); } catch{} }}
                onMouseLeave={(e) => { try { (e.currentTarget as HTMLImageElement).src = it.image; } catch{} }}
              />
              <div style={{flex:1}}>
                <div style={{fontSize:12, fontWeight:600}}>{it.name}</div>
                <div style={{fontSize:11}}>{it.price}</div>
                <div style={{display:'flex', alignItems:'center', gap:4, marginTop:4}}>
                  <button onClick={()=> { removeFromCart(it.id); addToast('Removed','info'); }} style={{fontSize:11}}>✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <footer>
          <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:10}}>
            <label style={{display:'flex', alignItems:'center', gap:6, fontSize:12}}>
              <input type="checkbox" checked={intl} onChange={(e)=> setIntl(e.target.checked)} />
              Ship internationally
            </label>
            <span style={{fontSize:11, opacity:.6}}>(uncheck for US)</span>
          </div>
          <div className="cart-summary">
            <div className="row">
              <span>Subtotal</span>
              <span className={`subtotal-val ${bump ? 'subtotal-bump' : ''}`}>{formatCurrency(totalPrice)}</span>
            </div>
            {bogo > 0 && (
              <div className="row discount">
                <span>BOGO discount</span>
                <span>−{formatCurrency(bogo)}</span>
              </div>
            )}
            <div className="row total">
              <span>Est. total</span>
              <span>{formatCurrency(estimatedTotal)}</span>
            </div>
          </div>
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            <Link to="/cart" onClick={onClose} style={{fontSize:12, textDecoration:'underline'}}>View full cart →</Link>
            <span style={{flex:1}} />
            <button
              className="add-btn"
              onClick={() => startCheckout(items.map(i => ({ id: i.id, priceId: i.priceId })), intl)}
              disabled={items.length===0 || items.some(i => !i.priceId)}
              aria-disabled={items.length===0 || items.some(i => !i.priceId)}
            >
              Checkout
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

function useRouteSEO(){
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    let title = 'RAYBONE';
    let desc = 'Hand-finished small run apparel. Limited restocks.';
    if(path === '/contact'){ title = 'Contact – RAYBONE'; desc = 'Contact RAYBONE for order inquiries.'; }
    else if(path === '/about'){ title = 'About – RAYBONE'; desc = 'About RAYBONE'; }
  else if(path === '/cart'){ title = 'Cart – RAYBONE'; desc = 'Your cart and selected items.'; }
  else if(path === '/books'){ title = 'Books – RAYBONE'; desc = 'Downloadable books and documents.'; }
    else { title = 'raybone.clothing'; }
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if(metaDesc) metaDesc.setAttribute('content', desc);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if(ogTitle) ogTitle.setAttribute('content', title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if(ogDesc) ogDesc.setAttribute('content', desc);
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if(twTitle) twTitle.setAttribute('content', title);
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if(twDesc) twDesc.setAttribute('content', desc);
  }, [location]);
}

function useCanonicalAndUTM(){
  const location = useLocation();
  useEffect(() => {
    const base = window.location.origin;
    const path = location.pathname + (location.pathname.endsWith('/')? '' : '');
    const canonicalUrl = base + path;
    const linkEl = document.querySelector('link[data-dynamic-canonical]') as HTMLLinkElement | null;
    if(linkEl) linkEl.href = canonicalUrl;
  }, [location]);

  // First-touch UTM capture
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmKeys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'];
    const hasAny = utmKeys.some(k => params.get(k));
    if(hasAny){
      const existing = localStorage.getItem('utm_first_touch');
      if(!existing){
        const captured: Record<string,string> = {};
        utmKeys.forEach(k => { const v = params.get(k); if(v) captured[k]=v; });
        captured.timestamp = new Date().toISOString();
        localStorage.setItem('utm_first_touch', JSON.stringify(captured));
      }
    }
  }, [location]);
}

function AppShell(){
  const { totalItems, clearCart } = useCart();
  const location = useLocation();
  const [checkoutStatus, setCheckoutStatus] = useState<{type: 'success' | 'canceled' | null; message: string | null}>({ type: null, message: null });
  const lastFocused = useRef<HTMLElement | null>(null);
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const openMiniCart = useCallback(() => { lastFocused.current = document.activeElement as HTMLElement; setMiniCartOpen(true); }, []);
  const closeMiniCart = useCallback(() => { setMiniCartOpen(false); lastFocused.current?.focus(); }, []);

  // Entry gate state (respects the lock toggle)
  const [entered, setEntered] = useState<boolean>(() => {
    if (!ENTRY_LOCK_ENABLED) return true;
    try { return sessionStorage.getItem('entered') === '1'; } catch { return false; }
  });

  // Lock background scroll before entry (only when lock is enabled and not entered)
  useEffect(() => {
    if(ENTRY_LOCK_ENABLED && !entered){
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [entered]);

  // Effect: detect success=1, canceled=1 in query, act accordingly
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === '1') {
      clearCart();
      const url = window.location.pathname; // remove query
      window.history.replaceState({}, '', url);
      setCheckoutStatus({ type: 'success', message: 'Thank you for supporting Raybone Clothing!' });
    } else if (params.get('canceled') === '1') {
      const url = window.location.pathname || '/cart';
      window.history.replaceState({}, '', url);
      setCheckoutStatus({ type: 'canceled', message: 'Checkout canceled' });
    }
  }, [location, clearCart]);

  // Auto-dismiss confirmation after a short delay
  useEffect(() => {
    if (!checkoutStatus.type) return;
    const t = setTimeout(() => setCheckoutStatus({ type: null, message: null }), checkoutStatus.type === 'success' ? 7000 : 4000);
    return () => clearTimeout(t);
  }, [checkoutStatus]);

  const closeStatus = useCallback(() => setCheckoutStatus({ type: null, message: null }), []);

  useRouteSEO();
  useCanonicalAndUTM();

  // Open mini cart when a product is added (from ProductGrid)
  useEffect(() => {
    const handler = () => openMiniCart();
    window.addEventListener('open-mini-cart', handler as EventListener);
    return () => window.removeEventListener('open-mini-cart', handler as EventListener);
  }, [openMiniCart]);

  // If lock enabled and not entered, show entry overlay only
  if(ENTRY_LOCK_ENABLED && !entered){
    return (
      <EntryPage onEnter={() => { try { sessionStorage.setItem('entered','1'); } catch{}; setEntered(true); }} />
    );
  }

  return (
    <div className="app-shell" style={{ position:'relative', zIndex: 1 }}>
      {/* Confirmation modal for checkout result */}
      {checkoutStatus.type && (
        <>
          <div className="confirm-backdrop" onClick={closeStatus} aria-hidden="true" style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:999}} />
          <div
            className={`confirm-modal ${checkoutStatus.type}`}
            onClick={(e) => e.stopPropagation()} 
            role="dialog"
            aria-modal="true"
            aria-label={checkoutStatus.type === 'success' ? 'Order confirmation' : 'Checkout status'}
            style={{position:'fixed', left:'50%', top:'18%', transform:'translateX(-50%)', zIndex:1000, background:'#fff', color:'#111', padding:'18px 18px 14px', borderRadius:10, minWidth:280, maxWidth:'90vw', boxShadow:'0 10px 30px rgba(0,0,0,0.25)'}}
          >
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:12}}>
              <h3 style={{margin:0, fontSize:16, fontWeight:700}}>{checkoutStatus.type === 'success' ? 'Order confirmed' : 'Checkout canceled'}</h3>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeStatus();
                }} 
                aria-label="Close" 
                style={{border:'none', background:'transparent', fontSize:18, lineHeight:1, cursor:'pointer'}}
              >
                ✕
              </button>
            </div>
            <p style={{margin:'10px 0 8px', fontSize:12, color:'#444'}}>{checkoutStatus.message}</p>
            {checkoutStatus.type === 'success' && (
              <p style={{margin:'0 0 10px', fontSize:12, color:'#444'}}>A confirmation will be sent to your email. You can close this message.</p>
            )}
          </div>
        </>
      )}

      <a href="#main" className="skip-link">Skip to content</a>
      {SHOP_ENABLED && <div className="announce-bar">FREE SHIPPING ON US ORDERS</div>}
      <nav className="main-nav" aria-label="Primary">
        <div className="brand"><Link to="/"><img src="/namespace.png" alt="RAYBONE" width={200} height={60} decoding="async" style={{ display:'block' }} /></Link></div>
        <button className="nav-toggle" aria-label="Toggle menu" aria-expanded={document.documentElement.classList.contains('nav-open')}
          onClick={() => document.documentElement.classList.toggle('nav-open')}>☰</button>
        <ul className="nav-links" role="menubar" aria-label="Main navigation">
          <li className="nav-link-single "role="none"><Link role="menuitem" to="/shop" onClick={() => document.documentElement.classList.remove('nav-open')}>Home</Link></li>
          <li className="nav-link-single "role="none"><Link role="menuitem" to="/contact" onMouseEnter={preloadContact} onFocus={preloadContact} onClick={() => document.documentElement.classList.remove('nav-open')}>Contact</Link></li>
          <li className="nav-link-single "role="none"><Link role="menuitem" to="/about" onMouseEnter={preloadAbout} onFocus={preloadAbout} onClick={() => document.documentElement.classList.remove('nav-open')}>About</Link></li>
          <li className="nav-link-single "role="none"><Link role="menuitem" to="/books" onClick={() => document.documentElement.classList.remove('nav-open')}>Books</Link></li>
          <li className="nav-link-single "role="none"><Link role="menuitem" to="/resources" onClick={() => document.documentElement.classList.remove('nav-open')}>Resources</Link></li>
          {SHOP_ENABLED && <li role="none"><Link role="menuitem" to="/cart" onMouseEnter={preloadCart} onFocus={preloadCart} onClick={() => document.documentElement.classList.remove('nav-open')}>Cart</Link></li>}
        </ul>
        <div className="nav-actions">
          {SHOP_ENABLED && (
            <button className="icon-btn" aria-label="Open cart" onClick={openMiniCart} style={{display:'flex', alignItems:'center', gap:6}}>
              <ShoppingBag size={18} />
              {totalItems > 0 && <span style={{fontSize:11}} aria-live="polite" aria-atomic="true">{totalItems}</span>}
            </button>
          )}
        </div>
      </nav>
      {/* Overlay for small modal-style nav; visible when html has .nav-open */}
      <div className="mobile-nav-overlay" onClick={() => document.documentElement.classList.remove('nav-open')} aria-hidden="true" />
      {/* <div className={`status-banner ${checkoutStatus.type || ''}`} aria-live="polite">{checkoutStatus.message}</div> */}
      {/* Render hero only on /shop and above the content container */}
      {location.pathname === '/shop'}
      <MiniCart open={SHOP_ENABLED && miniCartOpen} onClose={closeMiniCart} />
      <div className="raw-container fractal-bg" id="main">
        <main>
          <Suspense fallback={<div style={{padding:40, fontSize:12}}>Loading…</div>}>
            <ErrorBoundary>
              <Routes>
                <Route
                  path="/"
                  element={ENTRY_LOCK_ENABLED
                    ? (<EntryPage onEnter={() => { try { sessionStorage.setItem('entered','1'); } catch{}; setEntered(true); }} />)
                    : (<Navigate to="/shop" replace />)
                  }
                />
                <Route element={<RequireEntered entered={ENTRY_LOCK_ENABLED ? entered : true} />}>
                  <Route path="/shop" element={SHOP_ENABLED ? <ProductGrid /> : <ShopClosed />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/cart" element={SHOP_ENABLED ? <CartPage /> : <Navigate to="/shop" replace />} />
                  <Route path="/books" element={<BooksPage />} />
                  <Route path="/resources" element={<ResourcesPage />} />
                </Route>
              </Routes>
            </ErrorBoundary>
          </Suspense>
          <footer className="site-footer">
            <p>© 2026 RAYBONE</p>
          </footer>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <CartProvider>
        <AppShell />
      </CartProvider>
    </ToastProvider>
  );
}

export default App;
